import Backbone from '@rover/globals/backbone';
import { pushEventWithDebug } from '@rover/globals/dataLayer';
import $ from '@rover/globals/jquery';
import Rover from '@rover/globals/Rover';
import _ from '@rover/globals/underscore';
import { isMobileBrowser } from '@rover/utilities/device';
import { authenticatePaymentIntent, createPaymentIntent } from '@rover/utilities/stripe.client';

function getCommonFlexibleTimes(dates) {
  let commonFlexTimes = null;
  for (const d of dates) {
    for (const ft of d.flexible_times) {
      if (commonFlexTimes == null) {
        commonFlexTimes = ft;
      } else if (
        commonFlexTimes.start_time_range.start !== ft.start_time_range.start ||
        commonFlexTimes.start_time_range.end !== ft.start_time_range.end ||
        commonFlexTimes.end_time_range.start !== ft.end_time_range.start ||
        commonFlexTimes.end_time_range.end !== ft.end_time_range.end
      ) {
        return null;
      }
    }
  }
  return commonFlexTimes;
}

export default Backbone.Model.extend({
  url() {
    return this.requestableModel.get('url');
  },
  initialize(options) {
    this.requestableModel = options.requestableModel;
    this.roverBucksModel = options.roverBucksModel;
    this.creditCardCollectionView = options.creditCardCollectionView;
    this.priceModel = options.priceModel;
  },
  async pay(options) {
    const paymentInfo = await this.getPaymentBreakdown(options);
    if (!paymentInfo) {
      return;
    }
    let data = {
      request: {
        status: '',
        request_version: null,
        payment_info: paymentInfo,
      },
      source: this.getSource(),
      booking_add_ons: this.requestableModel.getActiveBookingAddOns(),
      requester_add_ons: this.requestableModel.get('requester_add_ons'),
    };
    data = _.extend(data, this.requestableModel.getDatesData(), {
      time_ranges: this.requestableModel.get('time_ranges'),
    });
    if (
      this.requestableModel.get('service_type') === 'doggy-day-care' &&
      this.requestableModel.isRecurring
    ) {
      const activeConversation = this.requestableModel.get('active_conversation');
      const commonFlexibleTimes = getCommonFlexibleTimes(activeConversation.dates);
      if (commonFlexibleTimes) {
        data.days = data.days.map(day => ({
          ...day,
          flexible_times: [commonFlexibleTimes],
        }));
      }
    }
    const state = this.requestableModel.getState();

    if (state === 'pay') {
      data.request.status = 'accepted';
      data.request.request_version = this.requestableModel.get('request').request_version;
    } else if (state === 'authorize') {
      data.request.status = 'pending';
    } else {
      return false;
    }
    this.set('data', data);
    this.save(null, options);
    return true;
  },
  isNew() {
    // Always PUT
    return false;
  },
  toJSON() {
    // `data` is the only attribute we care about here
    return this.get('data');
  },
  getSource() {
    if ($('body').hasClass('js-mobile-embedded')) {
      return $('body').data('mobile-embedded-source');
    }
    return isMobileBrowser() ? 'mobile_web' : 'web';
  },
  /** see https://docs.google.com/presentation/d/1u0oZM5lCxiMbz1BQaGrVM2Mq8FtSoy-pwBxO4qB6BlE/
   */
  logPaymentIntentCreatedEvent(success, serviceType, conversationOpk) {
    pushEventWithDebug({
      event: 'ext-api-stripe-checkout-payment-intents-request-book',
      submitStatus: success ? 'success' : 'fail',
      usePaymentIntents: true,
      serviceType,
      conversationId: conversationOpk,
    });
  },
  /** see https://docs.google.com/presentation/d/1u0oZM5lCxiMbz1BQaGrVM2Mq8FtSoy-pwBxO4qB6BlE/
   */
  logPaymentIntentRequiredEvent(requiresAction, serviceType, conversationOpk) {
    if (requiresAction) {
      pushEventWithDebug({
        event: 'ext-api-stripe-checkout-modal-required',
        serviceType,
        conversationId: conversationOpk,
      });
    }
  },
  /** see https://docs.google.com/presentation/d/1u0oZM5lCxiMbz1BQaGrVM2Mq8FtSoy-pwBxO4qB6BlE/
   */
  logPaymentIntentCompletedEvent(success, serviceType, conversationOpk) {
    pushEventWithDebug({
      event: 'ext-api-stripe-checkout-modal-complete',
      submitStatus: success ? 'success' : 'fail',
      serviceType,
      conversationId: conversationOpk,
    });
  },
  async getPaymentBreakdown(options) {
    const totalPrice = this.priceModel.getTotalPrice();
    const selectedCC = this.creditCardCollectionView.getSelectedCC();
    const selectedCCURL = selectedCC.val();
    const usesPaymentIntent = selectedCC.data('uses-payment-intent');
    let ret = [];

    if (totalPrice.roverBucksToUse !== '0.00') {
      ret = ret.concat(this.spreadRoverBucks(totalPrice.roverBucksToUse));
    }

    if (this.priceModel.roverBucksCoverCost()) {
      return ret;
    }
    if (selectedCCURL) {
      const paymentInfo = {
        url: selectedCCURL,
        amount: totalPrice.amountToPay,
      };
      if (usesPaymentIntent) {
        const { paymentIntentError } = options;
        const serviceType = this.requestableModel.get('service_type');
        try {
          const { clientSecret, requiresAction, opk } = await createPaymentIntent(
            Rover.conversation_opk,
            selectedCCURL,
            totalPrice.amountForPaymentIntent,
            totalPrice.totalPriceCurrencyIso
          );
          this.logPaymentIntentCreatedEvent(true, serviceType, Rover.conversation_opk);
          this.logPaymentIntentRequiredEvent(requiresAction, serviceType, Rover.conversation_opk);
          paymentInfo.payment_intent_opk = await authenticatePaymentIntent(
            clientSecret,
            requiresAction,
            opk
          );
          this.logPaymentIntentCompletedEvent(true, serviceType, Rover.conversation_opk);
        } catch (e) {
          let message = null;
          if (e.type === 'PaymentIntentZeroAmountChargedError') {
            this.logPaymentIntentCreatedEvent(false, serviceType, Rover.conversation_opk);
            ({ message } = e);
          }
          if (e.type === 'PaymentIntentCreationError') {
            this.logPaymentIntentCreatedEvent(false, serviceType, Rover.conversation_opk);

            // since the error messages contain untranslated strings
            // which seem to be formatted differently
            // use the errorType to determine if it is meant
            // to be displayed to the user
            if (e.errorType === 'payment_intent_creation') {
              ({ message } = e);
            }
          }
          if (e.type === 'PaymentIntentAuthenticationError') {
            this.logPaymentIntentCompletedEvent(false, serviceType, Rover.conversation_opk);
          }
          paymentIntentError(message);
          return;
        }
      }

      ret.push(paymentInfo);
      return ret;
    }
    // No selected CC and no RoverBucks, so no payment breakdown
    return false;
  },
  spreadRoverBucks(amount) {
    let buckAmount = amount;
    const methods = [];
    const methodModels = _.values(this.roverBucksModel.getMethodModels());
    let i = 0;

    for (i = 0; i < methodModels.length; i += 1) {
      const method = methodModels[i];
      const amt = Math.min(buckAmount, method.getTotalRoverBucks());
      if (amt !== 0) {
        methods.push({
          url: method.url(),
          amount: amt.toFixed(2),
        });
        buckAmount -= amt;
        if (buckAmount <= 0) {
          break;
        }
      }
    }
    return methods;
  },
});



// WEBPACK FOOTER //
// ./js/backbone/models/PaymentModel.js